// Real product photos on the menus (phones, parts, consoles), each in the same white tile, and
// the drawn picture whenever a photo is missing or doesn't load.
import { useEffect, useState, type ReactNode } from 'react';
import { get } from '../lib/api';
import { matchModelPhoto, sizedPhoto } from '../../shared/photos';
import { DeviceArt } from './art';
import { Icon, type IconName } from './icons';

let index: [string, string][] | null = null;
let loading: Promise<[string, string][]> | null = null;

/** Every model's photo (loaded once per tablet, then from the browser's cache). */
function loadIndex(): Promise<[string, string][]> {
  if (index) return Promise.resolve(index);
  loading ??= get<{ models: [string, string][] }>('/api/photos/models')
    .then((r) => (index = r.models ?? []))
    .catch(() => {
      loading = null;
      return [];
    });
  return loading;
}

function usePhotoIndex(): [string, string][] | null {
  const [list, setList] = useState(index);
  useEffect(() => {
    if (list) return;
    let live = true;
    loadIndex().then((l) => live && setList(l));
    return () => { live = false; };
  }, [list]);
  return list;
}

/** A model's own photo, if the catalog has one. */
export function useModelPhoto(model: string | null | undefined): string | null {
  const list = usePhotoIndex();
  return list && model ? matchModelPhoto(list, model) : null;
}

/** A real photo, sized for where it's shown; `fallback` (the drawn picture) if it doesn't load. */
export function Photo({ src, alt, size, fallback = null, className = '' }: { src: string | null | undefined; alt: string; size: number; fallback?: ReactNode; className?: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  const url = sizedPhoto(src, size);
  if (!url || broken) return <>{fallback}</>;
  return <img className={`photo ${className}`} src={url} alt={alt} width={size} height={size} loading="lazy" decoding="async" onError={() => setBroken(true)} />;
}

/** The device's real photo by its model; the drawn phone in its color when there's none. */
export function ModelPhoto({ brand, model, color, category, size = 72 }: { brand?: string | null; model?: string | null; color?: string | null; category?: string | null; size?: number }) {
  const src = useModelPhoto(model);
  const art = <DeviceArt brand={brand} model={model} color={color} category={category} size={size} />;
  return src ? <Photo src={src} alt={[brand, model].filter(Boolean).join(' ')} size={size} fallback={art} /> : art;
}

/** A device in inventory: the store's own photo of it, else its model's photo, else the drawn phone in its color. */
export function DevicePhoto({ url, brand, model, color, category, size = 72 }: { url?: string | null; brand?: string | null; model?: string | null; color?: string | null; category?: string | null; size?: number }) {
  const fallback = <ModelPhoto brand={brand} model={model} color={color} category={category} size={size} />;
  return url ? <Photo src={url} alt={[brand, model].filter(Boolean).join(' ')} size={size} fallback={fallback} /> : fallback;
}

/** A menu picture: the photo in a white tile with a small blue badge for what the button does. */
export function PhotoTile({ src, alt, size = 64, icon, fallback }: { src: string | null | undefined; alt: string; size?: number; icon?: IconName; fallback?: ReactNode }) {
  const I = icon ? Icon[icon] : null;
  return (
    <span className="photo-tile" style={{ width: size, height: size }}>
      <Photo src={src} alt={alt} size={size} fallback={fallback} />
      {I ? <span className="photo-badge" aria-hidden="true"><I /></span> : null}
    </span>
  );
}
